import { EditorState } from "@codemirror/state";
import { EditorView, placeholder as cmPlaceholder } from "@codemirror/view";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { useDrag } from "@use-gesture/react";
import classNames from "classnames";
import {
  action,
  computed,
  IComputedValue,
  IObservableValue,
  reaction,
  runInAction,
  untracked,
} from "mobx";
import { observer } from "mobx-react-lite";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { STATE_PANE_GETTER_OUTPUT_INDEX } from "../Constants";
import {
  ActionType,
  EvaluateExpressionType,
  Pane,
  PaneExpression,
  PaneOutput,
  PaneType,
  PaneViewMode,
  StateControl,
} from "../Types";
import { FeatherIcon } from "../ui/FeatherIcon";
import { Tooltip } from "../ui/Tooltip";
import { assert, debounce, modifierKey, throttle, useSyncRef } from "../Utils";
import { canvasDispatch } from "./CanvasDispatch";
import {
  activePaneStates,
  canvasAtom,
  isViewingLayoutComputed,
  shouldShowCanvasWiresComputed,
  showPaneSettingsPaneIdAtom,
} from "./CanvasState";
import {
  cmExtensions,
  cmLanguageExtensions,
  PaneExpressionEditor,
} from "./ExpressionEditor";
import { JSONRenderRoot } from "./JSONRender";
import { dragValueAtom } from "./NewPane";
import { PaneBody } from "./PaneBody";
import { PaneTransformButton } from "./PaneHeader";
import { PaneHeaderBar } from "./PaneHeaderBar";
import { PaneSplit } from "./PaneSplit";
import { WAITING_CLASSNAME } from "./SandboxUtils";

function InvalidOutputType({
  paneId,
  output,
  validator,
}: {
  paneId: string;
  output: PaneOutput;
  validator: (value: any) => boolean;
}) {
  if (output[0] === "error") {
    return <div className="text-red-400 mt-4">{output[1].toString()}</div>;
  }
  if (output[0] !== "value" || validator(output[1])) {
    return null;
  }
  return (
    <div className="mt-4">
      <div className="text-neutral-400 text-2xs mb-1">
        current value does not match type
      </div>
      <JSONRenderRoot paneId={paneId} value={output[1]} />
    </div>
  );
}

const NumberInput = observer(
  ({
    paneId,
    step = 1,
    outputBox,
  }: {
    paneId: string;
    step: number | undefined;
    outputBox: IObservableValue<PaneOutput>;
  }) => {
    const output = outputBox.get();
    const [value, setValue] = useState(
      output[0] === "value" ? `${parseFloat(output[1])}` : "0"
    );
    const valueRef = useSyncRef(value);
    const [setOutputDebounced] = useState<(v: any) => void>(() =>
      debounce(
        action((v: any) => {
          if (outputBox.get()[0] !== "value" || outputBox.get()[1] !== v) {
            outputBox.set(["value", v]);
          }
        }),
        50
      )
    );
    const inputRef = useRef<HTMLInputElement | null>(null);
    useEffect(() => {
      if (
        document.activeElement !== inputRef.current &&
        output[0] === "value" &&
        parseFloat(value) !== output[1]
      ) {
        setValue(`${parseFloat(output[1])}`);
      }
    }, [output]);

    const [isDragging, setIsDragging] = useState(false);
    const bindDrag = useDrag(({ active, last, delta: [dx], memo = 0 }) => {
      setIsDragging(active);
      if (last) {
        return;
      }
      memo += dx / 2;
      if (Math.abs(memo) >= 1) {
        const newValueNum =
          parseFloat(valueRef.current) + Math.floor(memo) * step;
        const newValue = `${newValueNum}`;
        setValue(newValue);
        setOutputDebounced(newValueNum);
        memo = 0;
      }
      return memo;
    });

    return (
      <div className="">
        <div
          className={classNames(
            "flex border border-neutral-200 hover:border-primary-400 focus-within:border-primary-400 rounded-sm overflow-hidden transition",
            { "select-none": isDragging }
          )}
        >
          <div
            {...bindDrag()}
            className={classNames(
              "p-1 cursor-ew-resize touch-none transition",
              isDragging
                ? "bg-primary-400 text-neutral-0"
                : "text-neutral-400 hover:text-primary-400"
            )}
          >
            v
          </div>
          <input
            type="text"
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              setOutputDebounced(parseFloat(e.target.value));
            }}
            onBlur={(e) => {
              const newValue = parseFloat(e.target.value);
              if (`${newValue}` !== value) {
                setValue(`${newValue}`);
                setOutputDebounced(newValue);
              }
            }}
            className="grow bg-neutral-0 text-right outline-none pr-2"
            ref={inputRef}
          />
        </div>
        <InvalidOutputType
          paneId={paneId}
          output={output}
          validator={(v) => typeof v === "number"}
        />
      </div>
    );
  }
);

const SCRUBBER_WIDTH = 8;
function clamp(value: number, min: number, max: number) {
  return Math.max(Math.min(value, max), min);
}
function sanitizeStep(value: number, step: number, min: number) {
  const steps = Math.round((value - min) / step);
  let sanitized = min + steps * step;
  if (min === 0) {
    // round floating point math numbers
    if (step === 0.1) {
      sanitized = Math.round(sanitized * 10) / 10;
    } else if (step === 0.01) {
      sanitized = Math.round(sanitized * 100) / 100;
    }
  }
  return sanitized;
}

export const NumberRangeInput = observer(
  ({
    paneId,
    min,
    max,
    step,
    outputBox,
  }: {
    paneId?: string;
    min: number;
    max: number;
    step: number | undefined;
    outputBox: IObservableValue<PaneOutput>;
  }) => {
    const output = outputBox.get();

    const rangeRef = useRef<HTMLDivElement | null>(null);
    const scrubberRef = useRef<HTMLDivElement | null>(null);
    const rangeWidthRef = useRef<number>(0);

    const [value, setValue] = useState(
      output[0] === "value" ? `${parseFloat(output[1])}` : "0"
    );
    const valueNum = parseFloat(value);
    const [setOutputDebounced] = useState(() =>
      debounce(
        action((v: any) => {
          if (outputBox.get()[0] !== "value" || outputBox.get()[1] !== v) {
            outputBox.set(["value", v]);
          }
        }),
        50
      )
    );
    const [isDragging, setIsDragging] = useState(false);
    const inputRef = useRef<HTMLInputElement | null>(null);
    useEffect(() => {
      if (
        document.activeElement !== inputRef.current &&
        !isDragging &&
        output[0] === "value" &&
        parseFloat(value) !== output[1]
      ) {
        setValue(`${parseFloat(output[1])}`);
      }
    }, [output]);

    const bindDrag = useDrag(
      ({ event, first, last, xy: [x], movement: [mx], memo }) => {
        if (first) {
          const { width, left } = rangeRef.current!.getBoundingClientRect();
          rangeWidthRef.current = width - SCRUBBER_WIDTH;

          const targetIsScrub = event?.target === scrubberRef.current;
          memo = targetIsScrub
            ? parseFloat(value)
            : min + ((x - left) / width) * (max - min);
          setIsDragging(true);
        }
        if (last) {
          setIsDragging(false);
        }
        let newValue =
          Math.round(
            (memo + (mx / rangeWidthRef.current) * (max - min)) * 100
          ) / 100;
        newValue = clamp(newValue, min, max);
        if (step !== undefined) {
          newValue = sanitizeStep(newValue, step, min);
        }
        setValue(`${newValue}`);
        setOutputDebounced(newValue);
        return memo;
      }
    );

    const valuePercentage = (valueNum - min) / (max - min);

    return (
      <div className="">
        <div className="flex items-center gap-2">
          <div
            {...bindDrag()}
            className="relative grow touch-none flex cursor-pointer h-4 group"
            ref={rangeRef}
          >
            <div className="absolute left-0 right-0 h-0.5 bg-neutral-100 self-center transition rounded-sm"></div>
            <div
              className={classNames(
                "absolute left-0 h-0.5 bg-neutral-300 group-hover:bg-primary-300 self-center transition rounded-sm",
                { hidden: valuePercentage < 0 || valuePercentage > 1 }
              )}
              style={{ width: `${valuePercentage * 100}%` }}
            ></div>
            <div
              className={classNames(
                "absolute w-2 top-0 bottom-0 bg-neutral-400 group-hover:bg-primary-400 shadow-scrubber hover:bg-primary-500 transition rounded-sm",
                output[0] === "waiting"
                  ? "shadow-neutral-50"
                  : "shadow-neutral-0",
                { hidden: valuePercentage < 0 || valuePercentage > 1 }
              )}
              style={{
                left: `calc(${valuePercentage} * (100% - ${SCRUBBER_WIDTH}px))`,
              }}
              ref={scrubberRef}
            ></div>
          </div>
          <input
            type="text"
            value={value}
            min={min}
            max={max}
            step={step}
            onChange={(e) => {
              setValue(e.target.value);
              setOutputDebounced(parseFloat(e.target.value));
            }}
            onBlur={(e) => {
              let newValue = clamp(parseFloat(e.target.value), min, max);
              if (step !== undefined) {
                newValue = sanitizeStep(newValue, step, min);
              }
              if (`${newValue}` !== value) {
                setValue(`${newValue}`);
                setOutputDebounced(newValue);
              }
            }}
            className={classNames(
              "border min-w-[4rem] w-1/5 px-2 py-1 rounded-sm text-right transition dark:bg-neutral-150",
              isDragging
                ? "border-primary-400"
                : "border-neutral-200 hover:border-primary-400 focus:border-primary-400"
            )}
            ref={inputRef}
          />
        </div>
        {paneId !== undefined ? (
          <InvalidOutputType
            paneId={paneId}
            output={output}
            validator={(v) => typeof v === "number"}
          />
        ) : null}
      </div>
    );
  }
);

const BooleanInput = observer(
  ({
    paneId,
    outputBox,
  }: {
    paneId: string;
    outputBox: IObservableValue<PaneOutput>;
  }) => {
    const output = outputBox.get();
    const isTrue = output[0] === "value" && output[1] === true;

    return (
      <div className="">
        <input
          type="checkbox"
          checked={isTrue}
          onChange={action((e) => {
            outputBox.set(["value", e.target.checked]);
          })}
        />
        <InvalidOutputType
          paneId={paneId}
          output={output}
          validator={(v) => typeof v === "boolean"}
        />
      </div>
    );
  }
);

const SelectInput = observer(
  ({
    paneId,
    options,
    outputBox,
  }: {
    paneId: string;
    options: { optionsExpression: string };
    outputBox: IObservableValue<PaneOutput>;
  }) => {
    const output = outputBox.get();
    const selectOptions: [labels: string[], values: any[]] | undefined =
      useMemo(() => {
        try {
          const f = new Function(`return ${options.optionsExpression}`);
          const selectOptions = f();
          if (Array.isArray(selectOptions)) {
            return [selectOptions, selectOptions];
          }
          return [Object.keys(selectOptions), Object.values(selectOptions)];
        } catch {
          return undefined;
        }
      }, [options.optionsExpression]);

    const selectedIndex =
      output[0] === "value" && selectOptions !== undefined
        ? selectOptions[1].indexOf(output[1])
        : -1;

    return (
      <div className="">
        <select
          value={selectedIndex}
          onChange={action((e) => {
            const value = selectOptions![1][parseInt(e.target.value)];
            outputBox.set(["value", value]);
          })}
          className="w-full border border-neutral-200 p-1 rounded-sm bg-neutral-50 focus:bg-neutral-100 transition"
        >
          {selectOptions !== undefined
            ? selectOptions[0].map((label, i) => (
                <option value={i} key={i}>
                  {label}
                </option>
              ))
            : null}
        </select>
        <InvalidOutputType
          paneId={paneId}
          output={output}
          validator={(v) =>
            selectOptions === undefined || selectOptions[1].includes(v)
          }
        />
      </div>
    );
  }
);

const TextInput = observer(
  ({
    paneId,
    outputBox,
  }: {
    paneId: string;
    outputBox: IObservableValue<PaneOutput>;
  }) => {
    const output = outputBox.get();
    return (
      <>
        {output[0] === "value" && typeof output[1] !== "string" ? (
          <div className="p-2 border-b border-neutral-150">
            <div className="text-neutral-400 text-2xs mb-1">
              current value does not match type
            </div>
            <JSONRenderRoot paneId={paneId} value={output[1]} />
          </div>
        ) : null}
        <textarea
          value={
            output[0] === "value" && typeof output[1] === "string"
              ? output[1]
              : ""
          }
          placeholder="type something..."
          onChange={action((e) => {
            outputBox.set(["value", e.target.value]);
          })}
          spellCheck={false}
          className="block bg-transparent w-full h-full p-2 resize-none no-focus-visible"
        ></textarea>
      </>
    );
  }
);

function validatePaneControl(paneControl: StateControl | undefined) {
  if (paneControl === undefined) {
    return undefined;
  }
  if (paneControl[0] === "number" && paneControl[1] !== undefined) {
    const { min, max } = paneControl[1];
    if (typeof min === "number") {
      if (typeof max !== "number") {
        return "need max with min";
      } else if (min > max) {
        return "min needs to be less than max";
      }
    } else if (typeof max === "number") {
      return "need min with max";
    }
  }
  if (paneControl[0] === "select") {
    const { optionsExpression } = paneControl[1];
    try {
      const f = new Function(`return ${optionsExpression}`);
      const selectOptions = f();
      if (
        Array.isArray(selectOptions) &&
        selectOptions.some((v) => typeof v !== "string")
      ) {
        return "option array elements must be strings";
      }
    } catch {
      return "options expression is invalid";
    }
  }
}

function arePaneControlsEqual(
  a: StateControl | undefined,
  b: StateControl | undefined
) {
  if (a === undefined && b === undefined) {
    return true;
  }
  if (a === undefined || b === undefined) {
    return false;
  }
  if (a[0] !== b[0]) {
    return false;
  }
  if (a[0] === "number" && b[0] === "number") {
    return (
      a[1]?.min === b[1]?.min &&
      a[1]?.max === b[1]?.max &&
      a[1]?.step === b[1]?.step
    );
  }
  if (a[0] === "select" && b[0] === "select") {
    return a[1].optionsExpression === b[1].optionsExpression;
  }
  return true;
}

function SelectOptionsExpressionEditor({
  initialValue,
  onChange,
}: {
  initialValue: string;
  onChange: (newValue: string) => void;
}) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const cmView = new EditorView({
      state: EditorState.create({
        doc: initialValue,
        extensions: [
          EditorView.theme({
            ".cm-content": { padding: "7px 0" },
          }),
          ...cmExtensions,
          ...cmLanguageExtensions,
          cmPlaceholder("javascript expression"),
        ],
      }),
      dispatch: (transaction) => {
        cmView.update([transaction]);
        const newValue = cmView.state.doc.sliceString(0);
        onChange(newValue);
      },
      parent: rootRef.current!,
    });
    return () => {
      cmView.destroy();
    };
  }, []);
  return (
    <div
      className="min-h-[3rem] border border-neutral-200 focus-within:border-neutral-300 bg-neutral-50 rounded transition"
      ref={rootRef}
    ></div>
  );
}

const FormNumberInputClassName =
  "bg-neutral-0 w-16 border border-neutral-200 focus:border-neutral-300 transition rounded-sm p-1";

const PaneStateTypeControlSettings = observer(
  ({
    paneId,
    paneComputed,
  }: {
    paneId: string;
    paneComputed: IComputedValue<
      Extract<Pane, { type: PaneType.State }> | undefined
    >;
  }) => {
    const paneControlComputed = computed(() => paneComputed.get()?.control);
    const [paneControl, setPaneControl] = useState(() =>
      untracked(() => paneControlComputed.get())
    );
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const validationError = validatePaneControl(paneControl);
    const hasChange = computed(
      () => !arePaneControlsEqual(paneControl, paneControlComputed.get())
    ).get();

    return (
      <div
        className={classNames(
          "w-56 bg-neutral-0 shadow-lg transition-opacity transform flex flex-col rounded select-none",
          paneControl?.[0] === "select" || paneControl?.[0] === "number"
            ? "h-64"
            : "h-32"
        )}
      >
        <div className="flex shrink-0 items-center justify-between h-8 pl-2 pr-1 border-b border-neutral-150">
          <span className="text-neutral-400">state pane settings</span>
          <PopoverPrimitive.Close asChild={true}>
            <button className="flex p-1 text-neutral-300 hover:text-neutral-400 transition">
              <FeatherIcon icon="x" size="md" />
            </button>
          </PopoverPrimitive.Close>
        </div>
        <div className="grow overflow-auto px-2 pt-2 pb-4">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (validationError !== undefined) {
                setHasSubmitted(true);
                return;
              }
              if (hasChange) {
                canvasDispatch([
                  ActionType.SetPaneStateControl,
                  {
                    paneId,
                    control: paneControl,
                  },
                ]);
              }
              if (showPaneSettingsPaneIdAtom.value === paneId) {
                runInAction(() => {
                  showPaneSettingsPaneIdAtom.value = undefined;
                });
              }
            }}
          >
            <div className="text-sm font-medium mb-2">control type</div>
            <div>
              <select
                onChange={(e) => {
                  const type = e.target.value as
                    | "number"
                    | "boolean"
                    | "select"
                    | "text"
                    | "none";
                  const newPaneControl: StateControl | undefined =
                    type === "select"
                      ? ["select", { optionsExpression: "" }]
                      : type !== "none"
                      ? [type]
                      : undefined;
                  setPaneControl(newPaneControl);
                  canvasDispatch([
                    ActionType.SetPaneStateControl,
                    {
                      paneId,
                      control: newPaneControl,
                    },
                  ]);
                }}
                value={paneControl?.[0] ?? "none"}
                className="block w-full bg-neutral-100 border border-neutral-200 focus:border-neutral-300 transition p-1 rounded-sm mb-2"
              >
                <option value="none">none</option>
                <option value="number">number</option>
                <option value="boolean">boolean</option>
                <option value="text">text</option>
                <option value="select">select</option>
              </select>
            </div>
            {paneControl?.[0] === "number" ? (
              <>
                <label className="flex items-center justify-between text-neutral-500 focus-within:text-neutral-700 mb-2">
                  step (optional)
                  <input
                    type="number"
                    value={paneControl[1]?.step ?? ""}
                    onChange={(e) => {
                      const value =
                        e.target.value === ""
                          ? undefined
                          : parseFloat(e.target.value);
                      setPaneControl((control) => {
                        assert(control?.[0] === "number");
                        return [control[0], { ...control[1], step: value }];
                      });
                    }}
                    className={FormNumberInputClassName}
                  />
                </label>
                <div className="border-t border-neutral-200 pt-2 mb-2 text-neutral-500">
                  range (optional)
                </div>
                <div className="flex gap-4 justify-between mb-4">
                  <label className="flex gap-2 items-center text-neutral-500 focus-within:text-neutral-700">
                    min
                    <input
                      type="number"
                      value={paneControl[1]?.min ?? ""}
                      onChange={(e) => {
                        const value =
                          e.target.value === ""
                            ? undefined
                            : parseFloat(e.target.value);
                        setPaneControl((control) => {
                          assert(control?.[0] === "number");
                          return [control[0], { ...control[1], min: value }];
                        });
                      }}
                      className={FormNumberInputClassName}
                    />
                  </label>
                  <label className="flex gap-2 items-center text-neutral-500 focus-within:text-neutral-700">
                    max
                    <input
                      type="number"
                      value={paneControl[1]?.max ?? ""}
                      onChange={(e) => {
                        const value =
                          e.target.value === ""
                            ? undefined
                            : parseFloat(e.target.value);
                        setPaneControl((control) => {
                          assert(control?.[0] === "number");
                          return [control[0], { ...control[1], max: value }];
                        });
                      }}
                      className={FormNumberInputClassName}
                    />
                  </label>
                </div>
              </>
            ) : paneControl?.[0] === "select" ? (
              <>
                <div className="mb-1 font-medium text-neutral-500">options</div>
                <div className="mb-2 text-neutral-400">
                  string array or object mapping labels to values
                </div>
                <div className="mb-2">
                  <SelectOptionsExpressionEditor
                    initialValue={paneControl[1].optionsExpression}
                    onChange={(optionsExpression) => {
                      setPaneControl((control) => {
                        assert(control?.[0] === "select");
                        return ["select", { ...control[1], optionsExpression }];
                      });
                    }}
                  />
                </div>
              </>
            ) : null}
            {paneControl?.[0] === "select" || paneControl?.[0] === "number" ? (
              <div>
                <button
                  className={classNames(
                    "button-colorless",
                    hasChange
                      ? "button-dark-color"
                      : "bg-neutral-100 text-neutral-500"
                  )}
                  disabled={!hasChange}
                >
                  update
                </button>
                {hasSubmitted && validationError !== undefined ? (
                  <div className="text-red-400 mt-1">{validationError}</div>
                ) : null}
              </div>
            ) : null}
          </form>
        </div>
      </div>
    );
  }
);

const PaneStateSettingsButton = observer(
  ({
    paneId,
    paneComputed,
  }: {
    paneId: string;
    paneComputed: IComputedValue<
      Extract<Pane, { type: PaneType.State }> | undefined
    >;
  }) => {
    const showSettings = computed(
      () => showPaneSettingsPaneIdAtom.value === paneId
    ).get();
    const wasSettingsShownRecently = useRef(showSettings);
    useEffect(() => {
      if (showSettings) {
        wasSettingsShownRecently.current = true;
      } else {
        const timeout = setTimeout(() => {
          wasSettingsShownRecently.current = false;
        }, 100);
        return () => {
          clearTimeout(timeout);
        };
      }
    }, [showSettings]);

    return (
      <>
        <Tooltip content="settings">
          <PopoverPrimitive.Trigger asChild={true}>
            <button
              className={classNames(
                "flex transition-colors p-1 ml-2 -mr-2 rounded",
                computed(() => {
                  const pane = paneComputed.get();
                  return pane?.control !== undefined;
                }).get()
                  ? "text-primary-400 hover:text-primary-500"
                  : "text-neutral-400 hover:text-neutral-600"
              )}
              onFocus={(e) => {
                if (wasSettingsShownRecently.current) {
                  // prevent tooltip from showing when popover closes
                  e.preventDefault();
                }
              }}
            >
              <FeatherIcon icon="sliders" size="sm" />
            </button>
          </PopoverPrimitive.Trigger>
        </Tooltip>
        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Content
            side="left"
            align="start"
            sideOffset={8}
            className="popover-scale-in"
          >
            <PaneStateTypeControlSettings
              paneId={paneId}
              paneComputed={paneComputed}
            />
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </>
    );
  }
);

export const PaneStateType = observer(
  ({
    paneId,
    paneComputed,
    isMaximized,
    toggleMaximize,
  }: {
    paneId: string;
    paneComputed: IComputedValue<
      Extract<Pane, { type: PaneType.State }> | undefined
    >;
    isMaximized: boolean;
    toggleMaximize: (action: any) => void;
  }) => {
    const activePaneState = activePaneStates.get(paneId)!;
    assert(activePaneState.type === PaneType.State);
    const outputBox = activePaneState.outputBoxes[0];
    const output = outputBox.get();

    const shouldShowCanvasWires = shouldShowCanvasWiresComputed.get();
    const paneControl = computed(() => paneComputed.get()?.control).get();
    const [setInitialExpressionThrottled] = useState(() =>
      throttle((expression: PaneExpression) => {
        canvasDispatch([
          ActionType.SetPaneInitialExpression,
          {
            paneId,
            initialExpression: expression,
          },
        ]);
      }, 100)
    );
    const isGetterReferenced = computed(
      () =>
        canvasAtom.value.panes.some(
          (p) =>
            ("inputs" in p &&
              p.inputs.some(
                (input) => input.source?.[0] === paneId && input.source[1] === 2
              )) ||
            ("templateInputSources" in p &&
              Object.values(p.templateInputSources).some(
                (source) => source[0] === paneId && source[1] === 2
              ))
        ) ||
        (dragValueAtom.value?.type === "reference" &&
          dragValueAtom.value.fromPaneId === paneId &&
          dragValueAtom.value.fromPaneOutputIndex === 2)
    ).get();
    const isInitialRenderRef = useRef(true);
    const editorRef = useRef<any | null>(null);
    useEffect(
      () =>
        reaction(
          () => paneComputed.get()?.initialExpression,
          (initialExpression) => {
            if (
              initialExpression !== undefined &&
              !isInitialRenderRef.current
            ) {
              editorRef.current?.setExpressionRemote(initialExpression);
            }
          }
        ),
      []
    );
    useEffect(() => {
      isInitialRenderRef.current = false;
    }, []);

    const viewMode = activePaneState.viewMode.get();

    const headerHeightRem = !shouldShowCanvasWires
      ? 8
      : isGetterReferenced
      ? 16
      : 12;
    return (
      <>
        <PopoverPrimitive.Root
          open={computed(
            () => showPaneSettingsPaneIdAtom.value === paneId
          ).get()}
          onOpenChange={action((open) => {
            if (open) {
              showPaneSettingsPaneIdAtom.value = paneId;
            } else if (showPaneSettingsPaneIdAtom.value === paneId) {
              showPaneSettingsPaneIdAtom.value = undefined;
            }
          })}
          modal={true}
        >
          <PopoverPrimitive.Anchor>
            <PaneHeaderBar
              paneId={paneId}
              paneComputed={paneComputed}
              heightClassName={
                headerHeightRem === 8
                  ? "h-8"
                  : headerHeightRem === 16
                  ? "h-16"
                  : "h-12"
              }
              isMaximized={isMaximized}
            >
              {activePaneState.viewMode.get() === PaneViewMode.Normal &&
              !isViewingLayoutComputed.get() ? (
                <PaneStateSettingsButton
                  paneId={paneId}
                  paneComputed={paneComputed}
                />
              ) : null}
              <div className="grow" />
              <div
                className={classNames(
                  "flex flex-col items-end",
                  isGetterReferenced ? "gap-px" : "gap-0.5"
                )}
              >
                {!isMaximized ? (
                  <>
                    <PaneTransformButton
                      paneId={paneId}
                      label="value"
                      outputIndex={0}
                      modifiedOutputIndex={STATE_PANE_GETTER_OUTPUT_INDEX}
                      tooltipText={
                        isGetterReferenced ? (
                          "reactive value"
                        ) : (
                          <div>
                            <div>
                              {modifierKey === "Meta" ? "⌘" : "ctrl"}-drag to
                              get
                            </div>
                            <div>non-reactive value</div>
                          </div>
                        )
                      }
                      paddingClassName="px-1"
                    />
                    <PaneTransformButton
                      paneId={paneId}
                      label="setValue"
                      outputIndex={1}
                      tooltipText={
                        <div>
                          <div>call with new value</div>
                          <div>or updater function</div>
                        </div>
                      }
                      paddingClassName="px-1"
                    />
                    {isGetterReferenced ? (
                      <PaneTransformButton
                        paneId={paneId}
                        label="getValue"
                        outputIndex={STATE_PANE_GETTER_OUTPUT_INDEX}
                        tooltipText={
                          <div>
                            <div>non-reactive function</div>
                            <div>to get state value</div>
                          </div>
                        }
                        paddingClassName="px-1"
                      />
                    ) : null}
                  </>
                ) : null}
              </div>
            </PaneHeaderBar>
          </PopoverPrimitive.Anchor>
        </PopoverPrimitive.Root>
        <PaneBody viewMode={viewMode} paneComputed={paneComputed}>
          <PaneSplit
            paneId={paneId}
            paneType={PaneType.State}
            activePaneState={activePaneState}
            isMaximized={isMaximized}
            inputElement={
              <div className="max-h-full overflow-auto">
                <div className="px-2 h-4 flex items-center justify-between relative top-1 uppercase text-2xs font-mono-natto text-neutral-400 select-none">
                  initial value
                </div>
                <PaneExpressionEditor
                  paneId={paneId}
                  paneComputed={paneComputed}
                  getInitialValue={() =>
                    paneComputed.get()?.initialExpression ?? ""
                  }
                  placeholder="expression"
                  onChange={setInitialExpressionThrottled}
                  focusOnMount={false}
                  toggleMaximize={toggleMaximize}
                  inspectorLogValuesBox={undefined}
                  expressionType={EvaluateExpressionType.Expression}
                  isStateTypePane={true}
                  ref={editorRef}
                />
              </div>
            }
            outputElement={
              <div
                className={classNames(
                  "h-full overflow-auto group select-text relative",
                  paneControl?.[0] !== "text" ? "p-2" : undefined,
                  output[0] === "waiting" ? WAITING_CLASSNAME : "bg-neutral-0"
                )}
              >
                {paneControl?.[0] === "number" ? (
                  paneControl[1]?.min !== undefined &&
                  paneControl[1]?.max !== undefined ? (
                    <NumberRangeInput
                      paneId={paneId}
                      min={paneControl[1].min}
                      max={paneControl[1].max}
                      step={paneControl[1].step}
                      outputBox={activePaneState.outputBoxes[0]}
                    />
                  ) : (
                    <NumberInput
                      paneId={paneId}
                      step={paneControl[1]?.step}
                      outputBox={activePaneState.outputBoxes[0]}
                    />
                  )
                ) : paneControl?.[0] === "boolean" ? (
                  <BooleanInput
                    paneId={paneId}
                    outputBox={activePaneState.outputBoxes[0]}
                  />
                ) : paneControl?.[0] === "text" ? (
                  <TextInput
                    paneId={paneId}
                    outputBox={activePaneState.outputBoxes[0]}
                  />
                ) : paneControl?.[0] === "select" ? (
                  <SelectInput
                    paneId={paneId}
                    options={paneControl[1]}
                    outputBox={activePaneState.outputBoxes[0]}
                  />
                ) : output[0] === "value" ? (
                  <JSONRenderRoot paneId={paneId} value={output[1]} />
                ) : output[0] === "error" ? (
                  <div className="text-red-400">{output[1].toString()}</div>
                ) : null}
              </div>
            }
          />
        </PaneBody>
      </>
    );
  }
);
